import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { 
  Users, Search, Filter, CheckCircle, XCircle, Clock, MoreVertical,
  UserCheck, UserX, Mail, Phone, Building2, Upload, Download, X, FileSpreadsheet
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEPARTMENT_LABELS = {
  admin: 'Admin',
  dm_channel: 'DM Channel',
  marketing: 'Marketing',
  legal: 'Legal',
  finance: 'Finance',
  operations: 'Operations',
  architect: 'Architect',
  it: 'IT',
  hr: 'HR',
  customer: 'Customer',
};

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  department_head: 'Dept. Head',
  dm: 'Development Manager',
  adm: 'Asst. Dev. Manager',
  cp: 'Channel Partner',
  employee: 'Employee',
  customer: 'Customer',
};

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'amber', icon: Clock },
  active: { label: 'Active', color: 'green', icon: CheckCircle },
  suspended: { label: 'Suspended', color: 'red', icon: XCircle },
  inactive: { label: 'Inactive', color: 'stone', icon: XCircle },
};

const UserManagement = () => {
  const { user: currentUser } = useOutletContext();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const bulkFileRef = useRef(null);
  const token = () => localStorage.getItem('crm_token');

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchQuery, statusFilter, departmentFilter]);

  const fetchUsers = async () => {
    const token = localStorage.getItem('crm_token');
    try {
      const response = await axios.get(`${API}/crm/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(u => 
        u.firstName?.toLowerCase().includes(query) ||
        u.lastName?.toLowerCase().includes(query) ||
        u.email?.toLowerCase().includes(query) ||
        u.employeeId?.toLowerCase().includes(query)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(u => u.status === statusFilter);
    }

    if (departmentFilter !== 'all') {
      filtered = filtered.filter(u => u.department === departmentFilter);
    }

    setFilteredUsers(filtered);
  };

  const updateUserStatus = async (userId, newStatus) => {
    const token = localStorage.getItem('crm_token');
    setActionLoading(true);
    try {
      await axios.patch(
        `${API}/crm/admin/users/${userId}/status?new_status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`User ${newStatus === 'active' ? 'approved' : 'updated'} successfully`);
      fetchUsers();
      setSelectedUser(null);
    } catch (error) {
      toast.error('Failed to update user status');
    } finally {
      setActionLoading(false);
    }
  };

  const pendingCount = users.filter(u => u.status === 'pending').length;
  const activeCount = users.filter(u => u.status === 'active').length;

  const handleBulkImport = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    setBulkImporting(true); setBulkResult(null);
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await fetch(`${API}/crm/admin/bulk-import-users`, {
        method: 'POST', headers: { Authorization: `Bearer ${token()}` }, body: fd,
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Import failed');
      setBulkResult(j);
      toast.success(`Created ${j.created} users, ${j.failed} skipped`);
      fetchUsers();
    } catch (err) { toast.error(err.message); }
    finally { setBulkImporting(false); }
  };

  const downloadTemplate = async () => {
    const r = await fetch(`${API}/crm/admin/bulk-import-template`, { headers: { Authorization: `Bearer ${token()}` } });
    const blob = await r.blob();
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = 'bulk_import_template.xlsx'; a.click(); URL.revokeObjectURL(a.href);
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-900"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-stone-900">User Management</h1>
          <p className="text-stone-500 mt-1">Manage and approve user registrations</p>
        </div>
        <div className="flex items-center gap-2 mt-4 md:mt-0">
          <input ref={bulkFileRef} type="file" accept=".xlsx,.xls" onChange={handleBulkImport} className="hidden" />
          <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="bulk-import-template-btn">
            <Download className="w-4 h-4 mr-1" /> Download Template
          </Button>
          <Button size="sm" onClick={() => bulkFileRef.current?.click()} disabled={bulkImporting}
            data-testid="bulk-import-btn"
            className="bg-indigo-600 hover:bg-indigo-700 text-white">
            <Upload className="w-4 h-4 mr-1" />{bulkImporting ? 'Importing…' : 'Bulk Import Team'}
          </Button>
        </div>
      </div>

      {/* Bulk Import Result */}
      {bulkResult && (
        <div className="mb-6 p-4 rounded-xl border border-indigo-100 bg-indigo-50" data-testid="bulk-import-result">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              <p className="font-bold text-indigo-700">
                Bulk Import Complete: {bulkResult.created} created, {bulkResult.failed} skipped
              </p>
              <p className="text-xs text-indigo-500">Default password: <code className="font-mono bg-white px-1 rounded">{bulkResult.default_password}</code></p>
            </div>
            <button onClick={() => setBulkResult(null)} className="text-indigo-400 hover:text-indigo-600"><X className="w-4 h-4" /></button>
          </div>
          {bulkResult.errors?.length > 0 && (
            <div className="mt-2">
              <p className="text-xs font-semibold text-red-600 mb-1">Errors:</p>
              {bulkResult.errors.map((e, i) => (
                <p key={i} className="text-xs text-red-600">• {e.row}: {e.reason}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Hierarchy Logins (DM / ADM / CP) */}
      <HierarchyLoginsPanel />

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-amber-100 rounded-full">
              <Clock className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-900">{pendingCount}</p>
              <p className="text-sm text-amber-700">Pending Approvals</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-full">
              <UserCheck className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-900">{activeCount}</p>
              <p className="text-sm text-green-700">Active Users</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-stone-50 border-stone-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-stone-100 rounded-full">
              <Users className="w-6 h-6 text-stone-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-900">{users.length}</p>
              <p className="text-sm text-stone-700">Total Users</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input
                placeholder="Search by name, email, or employee ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-full md:w-40">
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {Object.entries(DEPARTMENT_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Pending Approvals Section */}
      {pendingCount > 0 && statusFilter === 'all' && (
        <Card className="mb-6 border-amber-200">
          <CardHeader className="bg-amber-50">
            <CardTitle className="flex items-center gap-2 text-amber-900">
              <Clock className="w-5 h-5" />
              Pending Approvals ({pendingCount})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {users.filter(u => u.status === 'pending').map((user) => (
                <div key={user.id} className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:bg-stone-50">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center font-bold text-amber-700">
                      {user.firstName?.[0]}{user.lastName?.[0]}
                    </div>
                    <div>
                      <p className="font-medium text-stone-900">{user.firstName} {user.lastName}</p>
                      <p className="text-sm text-stone-500">{user.email}</p>
                      <p className="text-xs text-stone-400">
                        {DEPARTMENT_LABELS[user.department]} • {ROLE_LABELS[user.role]}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-16 md:ml-0">
                    <Button
                      size="sm"
                      onClick={() => updateUserStatus(user.id, 'active')}
                      disabled={actionLoading}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <UserCheck className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateUserStatus(user.id, 'suspended')}
                      disabled={actionLoading}
                      className="border-red-300 text-red-600 hover:bg-red-50"
                    >
                      <UserX className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Users ({filteredUsers.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50 border-y">
                <tr>
                  <th className="text-left p-4 font-medium text-stone-600">User</th>
                  <th className="text-left p-4 font-medium text-stone-600">Employee ID</th>
                  <th className="text-left p-4 font-medium text-stone-600">Department</th>
                  <th className="text-left p-4 font-medium text-stone-600">Role</th>
                  <th className="text-left p-4 font-medium text-stone-600">Status</th>
                  <th className="text-left p-4 font-medium text-stone-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-stone-500">
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => {
                    const StatusIcon = STATUS_CONFIG[user.status]?.icon || Clock;
                    return (
                      <tr key={user.id} className="hover:bg-stone-50">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center font-medium text-emerald-700">
                              {user.firstName?.[0]}{user.lastName?.[0]}
                            </div>
                            <div>
                              <p className="font-medium text-stone-900">{user.firstName} {user.lastName}</p>
                              <p className="text-xs text-stone-500">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="font-mono text-sm text-stone-600">{user.employeeId}</span>
                        </td>
                        <td className="p-4">
                          <span className="text-sm">{DEPARTMENT_LABELS[user.department]}</span>
                        </td>
                        <td className="p-4">
                          <span className="text-sm">{ROLE_LABELS[user.role]}</span>
                        </td>
                        <td className="p-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-${STATUS_CONFIG[user.status]?.color}-100 text-${STATUS_CONFIG[user.status]?.color}-700`}>
                            <StatusIcon className="w-3 h-3" />
                            {STATUS_CONFIG[user.status]?.label}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex gap-1">
                            {user.status === 'pending' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => updateUserStatus(user.id, 'active')}
                                  className="text-green-600 hover:bg-green-50"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => updateUserStatus(user.id, 'suspended')}
                                  className="text-red-600 hover:bg-red-50"
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            {user.status === 'active' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => updateUserStatus(user.id, 'suspended')}
                                className="text-amber-600 hover:bg-amber-50"
                              >
                                Suspend
                              </Button>
                            )}
                            {user.status === 'suspended' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => updateUserStatus(user.id, 'active')}
                                className="text-green-600 hover:bg-green-50"
                              >
                                Reactivate
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const HierarchyLoginsPanel = () => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const token = () => localStorage.getItem('crm_token');

  const handleExport = async () => {
    setBusy(true); setMsg('');
    try {
      const res = await fetch(`${API}/hierarchy-accounts/export`, { headers: { Authorization: `Bearer ${token()}` } });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'hierarchy_logins.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      setMsg('Exported credentials to Excel.');
    } catch (e) { setMsg('Export failed.'); toast.error('Export failed'); }
    setBusy(false);
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/hierarchy-accounts/import`, {
        method: 'POST', headers: { Authorization: `Bearer ${token()}` }, body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Import failed');
      setMsg(`Imported: ${data.updated} updated, ${data.not_found} not found. Mobile-number login is now enabled for updated accounts.`);
      toast.success(`Imported ${data.updated} accounts`);
    } catch (e) { setMsg(String(e.message || 'Import failed')); toast.error('Import failed'); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <Card className="mb-6 border-blue-200 bg-blue-50/40" data-testid="hierarchy-logins-panel">
      <CardContent className="p-5">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h3 className="font-semibold text-stone-800">Hierarchy Logins (DM / ADM / CP)</h3>
            <p className="text-sm text-stone-500 mt-0.5">
              Export all login IDs + password, or import an Excel (columns: <strong>ID, Name, Mobile</strong>) to set names & mobile numbers.
              Once a mobile is set, that person can log in with their <strong>mobile number</strong>.
            </p>
            {msg && <p className="text-xs text-blue-700 mt-2" data-testid="hierarchy-logins-msg">{msg}</p>}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} data-testid="hierarchy-import-input" />
            <Button variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="hierarchy-import-btn">
              Import Excel
            </Button>
            <Button disabled={busy} onClick={handleExport} data-testid="hierarchy-export-btn">
              Export Excel
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default UserManagement;
